import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import {
  getDefinition,
  listSteps, createStep, deleteStep, reorderSteps,
  listConditions, createCondition, deleteCondition,
  listApproverRules, createApproverRule, deleteApproverRule,
  listTat, createTat, deleteTat,
  validateDefinition,
  activateDefinition,
  WORKFLOW_STEP_TYPES, APPROVER_TYPES, CONDITION_OPERATORS, LOGICAL_CONJUNCTIONS,
  TAT_UNITS, ESCALATION_ACTIONS,
  type WorkflowDefinition, type WorkflowStep, type StepCondition, type ApproverRule, type TatConfig,
} from '../../api/workflowApi.js';
import { sx, Field, SelectField, CheckboxField, toOptions } from './wfKit.js';
import { listRoles, type Role } from '../../api/rolesApi.js';
import { Button } from '../../../../shared/components/Button.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { personLabel } from '../../components/ApprovalLevels.js';
import { UserPicker } from '../../components/UserPicker.js';

/** Sentinel for "start a fresh group", never sent to the API. */
const NEW_PARALLEL_GROUP = '__new__';

/**
 * WorkflowStepsDesignerPage — deep authoring for a single workflow definition
 * (UI Audit M04 C2 step/condition/approver builders + C5 TAT/escalation).
 *
 * One page, backed entirely by `/v1/wf/*`:
 *  - Steps: create / edit / delete / reorder (T046).
 *  - Per selected step, three self-contained editors:
 *      • Condition builder (T047)         — routing / skip conditions.
 *      • Approver resolution builder (T048) — dynamic approver rules.
 *      • TAT + escalation matrix (T049)   — SLA target + 3 escalation tiers.
 *  - Validate the whole definition structure before activation.
 */
export function WorkflowStepsDesignerPage(): ReactNode {
  const { id } = useParams<{ id: string }>();
  const { token } = useAuth();
  const [def, setDef] = useState<WorkflowDefinition | null>(null);
  const [steps, setSteps] = useState<WorkflowStep[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const [validation, setValidation] = useState<string[] | null>(null);
  /** The order the designer is arranging, before they commit to it. */
  const [order, setOrder] = useState<WorkflowStep[] | null>(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const loadSteps = useCallback(() => {
    if (!id) return;
    listSteps(token, id)
      .then((s) => { setSteps(s); setOrder(null); setError(null); })
      .catch((e) => setError((e as Error).message));
  }, [token, id]);

  useEffect(() => {
    if (!id) return;
    getDefinition(token, id).then(setDef).catch((e) => setError((e as Error).message));
    loadSteps();
  }, [token, id, loadSteps]);

  /**
   * Reordering used to persist on the click. An arrow pressed by accident was
   * already the live order of a workflow that may be governing approvals in
   * flight, with nothing to undo it and no moment at which the designer said
   * "this is what I meant". The new order is held here instead, and goes to
   * the server only when it is saved - the same stage-then-save shape the
   * approval setup screen already uses for its levels.
   */
  function move(step: WorkflowStep, dir: -1 | 1): void {
    if (!steps) return;
    const current = order ?? [...steps].sort((a, b) => a.stepSequence - b.stepSequence);
    const idx = current.findIndex((s) => s.stepId === step.stepId);
    const swapWith = idx + dir;
    if (idx < 0 || swapWith < 0 || swapWith >= current.length) return;
    const next = [...current];
    [next[idx], next[swapWith]] = [next[swapWith], next[idx]];
    setOrder(next);
    setNotice(null);
  }

  async function saveOrder(): Promise<void> {
    if (!order || !id || !steps) return;
    // The sequence numbers that exist today, reassigned in the new order, so
    // saving a reorder can never invent or drop one.
    const sequences = [...steps]
      .map((s) => s.stepSequence)
      .sort((a, b) => a - b);
    setSavingOrder(true); setError(null); setNotice(null);
    try {
      await reorderSteps(
        token,
        id,
        order.map((s, i) => ({ stepId: s.stepId, stepSequence: sequences[i] })),
      );
      setNotice('Step order saved.');
      loadSteps();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSavingOrder(false);
    }
  }

  async function removeStep(stepId: string): Promise<void> {
    try {
      await deleteStep(token, stepId);
      if (selectedStepId === stepId) setSelectedStepId(null);
      setConfirmDeleteId(null);
      setNotice('Step deleted.');
      loadSteps();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function runValidate(): Promise<void> {
    if (!id) return;
    setError(null); setNotice(null);
    try {
      const res = await validateDefinition(token, id);
      setValidation(res.errors);
      if (res.valid) setNotice('Structure is valid — ready to activate.');
    } catch (e) {
      setError((e as Error).message);
    }
  }

  /**
   * Activation, on the screen that declares the definition ready for it.
   *
   * The designer validated the structure and said "ready to activate", then
   * offered no way to do it: the journey ended one step short and the admin had
   * to find another screen to finish what this one had started. The endpoint
   * already existed and is the same one the approvals designer calls.
   */
  async function runActivate(): Promise<void> {
    if (!id) return;
    setError(null); setNotice(null);
    try {
      const res = await activateDefinition(token, id);
      // The API answers with the activated definition, or with the reasons it
      // refused. Both are the admin's answer, so neither is swallowed.
      if (res && typeof res === 'object' && 'errors' in res && Array.isArray(res.errors)) {
        setValidation(res.errors);
        setError('This definition cannot be activated yet.');
        return;
      }
      setValidation([]);
      setNotice('Workflow is active. It can now be bound to a process.');
      // Re-read so the header reflects the new status rather than the one the
      // page loaded with.
      setDef(await getDefinition(token, id));
    } catch (e) {
      setError((e as Error).message);
    }
  }

  const saved = steps ? [...steps].sort((a, b) => a.stepSequence - b.stepSequence) : null;
  // What the screen shows: the arrangement in progress if there is one, the
  // saved order otherwise.
  const sorted = order ?? saved;
  const orderChanged =
    order !== null &&
    saved !== null &&
    order.some((s, i) => s.stepId !== saved[i]?.stepId);
  const nextSeq = saved && saved.length ? Math.max(...saved.map((s) => s.stepSequence)) + 1 : 1;
  // A group id is a uuid, which tells a reader nothing. Numbering them in the
  // order they first appear is what lets someone see at a glance which steps
  // run together.
  const groupLabels = new Map<string, string>();
  for (const s of saved ?? []) {
    if (s.parallelGroupId && !groupLabels.has(s.parallelGroupId)) {
      groupLabels.set(s.parallelGroupId, `Group ${groupLabels.size + 1}`);
    }
  }

  return (
    <main aria-label="wf-steps-designer" style={sx.main}>
      <div style={sx.headerRow}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <Link to="/admin/workflows" style={sx.link}>← Designer</Link>
          <h1>{def ? `${def.workflowCode} — steps` : 'Workflow steps'}</h1>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button style={sx.secondaryBtn} aria-label="validate-definition" onClick={runValidate}>Validate</Button>
          {def?.workflowStatus === 'DRAFT' ? (
            <Button style={sx.secondaryBtn} aria-label="activate-definition" onClick={runActivate}>Activate</Button>
          ) : null}
        </div>
      </div>

      {error && <p role="alert" style={sx.error}>{error}</p>}
      {notice && <p style={sx.ok}>{notice}</p>}
      {validation && (
        validation.length === 0
          ? <p data-testid="validation-ok" style={sx.ok}>No structural issues found.</p>
          : (
            <ul data-testid="validation-errors" style={{ ...sx.error, paddingLeft: 20 }}>
              {validation.map((v, i) => <li key={i}>{v}</li>)}
            </ul>
          )
      )}

      <section aria-label="steps-section" style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 16 }}>Steps</h2>
        <p data-testid="initiator-note" style={{ fontSize: 13, color: 'var(--color-text-muted)', margin: '0 0 10px' }}>
          Starts with the person who submits the record. They are recorded as the
          initiator automatically, so there is no step to configure for them — but
          a later step can be sent back to them by choosing the approver type
          <strong> INITIATOR</strong>.
        </p>
        {orderChanged && (
          <div
            data-testid="order-unsaved"
            style={{ ...sx.card, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', margin: '0 0 10px' }}
          >
            <strong>Step order changed.</strong>
            <span style={{ color: 'var(--color-text-muted)' }}>
              Nothing is saved until you say so.
            </span>
            <Button
              style={sx.primaryBtn}
              aria-label="save-step-order"
              disabled={savingOrder}
              onClick={() => { void saveOrder(); }}
            >
              {savingOrder ? 'Saving…' : 'Save order'}
            </Button>
            <Button
              style={sx.secondaryBtn}
              aria-label="discard-step-order"
              disabled={savingOrder}
              onClick={() => { setOrder(null); setNotice(null); }}
            >
              Discard
            </Button>
          </div>
        )}
        {!sorted ? <p>Loading…</p> : sorted.length === 0 ? (
          <p data-testid="steps-empty" style={{ color: 'var(--color-text-muted)' }}>No steps yet. Add the first step below.</p>
        ) : (
          <table aria-label="steps-table" style={sx.table}>
            <thead>
              <tr style={sx.theadTr}>
                <th style={sx.th}>Seq</th>
                <th style={sx.th}>Name</th>
                <th style={sx.th}>Type</th>
                <th style={sx.th}>Approver</th>
                <th style={sx.th}>Parallel</th>
                <th style={sx.thRight}>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr data-testid="step-row-initiator" style={{ ...sx.tbodyTr, color: 'var(--color-text-muted)' }}>
                <td style={sx.td}>0</td>
                <td style={sx.td}>Submitted by initiator</td>
                <td style={sx.td}>START</td>
                <td style={sx.td}>Whoever raises the record</td>
                <td style={sx.td}>—</td>
                <td style={sx.tdRight}>—</td>
              </tr>
              {sorted.map((s, i) => (
                <tr key={s.stepId} data-testid={`step-row-${s.stepId}`} style={sx.tbodyTr}>
                  {/* While an order is being arranged the saved sequence number
                      would contradict the row it sits on, so the position is
                      shown instead and the saved number beside it. */}
                  <td style={sx.td}>
                    {orderChanged ? (
                      <span data-testid={`seq-${s.stepId}`}>
                        {i + 1}
                        <span style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>
                          {' '}(was {s.stepSequence})
                        </span>
                      </span>
                    ) : (
                      <span data-testid={`seq-${s.stepId}`}>{s.stepSequence}</span>
                    )}
                  </td>
                  <td style={sx.td}>{s.stepName}</td>
                  <td style={sx.td}>{s.stepType}</td>
                  <td style={sx.td}>{s.approverType ?? '—'}{s.approverRoleCode ? ` (${s.approverRoleCode})` : ''}</td>
                  <td style={sx.td} data-testid={`parallel-${s.stepId}`}>
                    {s.parallelGroupId ? groupLabels.get(s.parallelGroupId) ?? 'Group' : '—'}
                  </td>
                  <td style={sx.tdRight}>
                    <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <Button style={sx.secondaryBtn} aria-label={`up-${s.stepId}`} disabled={i === 0} onClick={() => move(s, -1)}>↑</Button>
                      <Button style={sx.secondaryBtn} aria-label={`down-${s.stepId}`} disabled={i === sorted.length - 1} onClick={() => move(s, 1)}>↓</Button>
                      <Button style={sx.secondaryBtn} aria-label={`configure-${s.stepId}`}
                        onClick={() => setSelectedStepId(selectedStepId === s.stepId ? null : s.stepId)}>
                        {selectedStepId === s.stepId ? 'Close' : 'Configure'}
                      </Button>
                      {/* Deleting a step of a live workflow used to happen on
                          the click, with nothing to undo it. Disabled while an
                          order is being arranged, because deleting reloads the
                          list and would throw that arrangement away unasked. */}
                      {confirmDeleteId === s.stepId ? (
                        <>
                          <Button style={sx.dangerBtn} aria-label={`confirm-delete-step-${s.stepId}`}
                            onClick={() => { void removeStep(s.stepId); }}>
                            Confirm delete
                          </Button>
                          <Button style={sx.secondaryBtn} aria-label={`cancel-delete-step-${s.stepId}`}
                            onClick={() => setConfirmDeleteId(null)}>
                            Keep it
                          </Button>
                        </>
                      ) : (
                        <Button style={sx.dangerBtn} aria-label={`delete-step-${s.stepId}`}
                          disabled={orderChanged}
                          onClick={() => { setConfirmDeleteId(s.stepId); setNotice(null); }}>
                          Delete
                        </Button>
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Adding a step reloads the list, which would silently throw away an
          arrangement in progress. The designer is asked to settle it first. */}
      {id && (orderChanged ? (
        <p data-testid="add-blocked-by-order" style={sx.error}>
          Save or discard the step order before adding another step.
        </p>
      ) : (
        <AddStepForm
          definitionId={id}
          nextSeq={nextSeq}
          isParallelAllowed={def?.isParallelAllowed === true}
          existingGroups={[...new Set((saved ?? []).map((s) => s.parallelGroupId).filter((g): g is string => Boolean(g)))]}
          onAdded={() => { setNotice('Step added.'); loadSteps(); }}
          onError={setError}
        />
      ))}

      {selectedStepId && (
        <StepConfigPanel key={selectedStepId} stepId={selectedStepId} onError={setError} />
      )}
    </main>
  );
}

// ── Add-step inline form ──────────────────────────────────────────────────────
function AddStepForm({ definitionId, nextSeq, isParallelAllowed, existingGroups, onAdded, onError }: {
  readonly definitionId: string;
  readonly nextSeq: number;
  readonly isParallelAllowed: boolean;
  readonly existingGroups: readonly string[];
  readonly onAdded: () => void;
  readonly onError: (m: string) => void;
}): ReactNode {
  const { token } = useAuth();
  const [stepName, setStepName] = useState('');
  const [stepType, setStepType] = useState<string>('APPROVAL');
  const [approverType, setApproverType] = useState('');
  const [approverRoleCode, setApproverRoleCode] = useState('');
  const [allowDelegate, setAllowDelegate] = useState(false);
  const [parallelGroup, setParallelGroup] = useState('');
  const [roles, setRoles] = useState<readonly Role[] | null>(null);
  const [rolesError, setRolesError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The role was a free-text box, so an administrator had to remember a code
  // and learned of a typo only after pressing Add step — the backend refuses
  // an unknown role (EX-WFS-06) and one nobody holds (EX-WFS-07). Offering the
  // tenant's own roles means the question can only be answered correctly.
  useEffect(() => {
    let alive = true;
    listRoles(token)
      .then((r) => { if (alive) setRoles(r); })
      // Said beside the field: the page error is cleared by the step reload
      // that runs alongside this read.
      .catch((e) => { if (alive) { setRoles([]); setRolesError((e as Error).message); } });
    return () => { alive = false; };
  }, [token]);

  // A role only decides anything for these two. Showing the field beside
  // HIERARCHY or INITIATOR invited an answer that would be ignored.
  const roleApplies =
    approverType === 'ROLE_BASED' || approverType === 'SPECIFIC_USER';

  // A parallel group opens its steps together so their approvers can decide at
  // the same time, and the level is carried only when every one of them has
  // approved. The workflow's own "Allow parallel approvals" decides whether
  // that is offered at all, so an untick leaves no way to configure one — and
  // the structure cannot contradict the setting.
  const groupOptions = [
    ...existingGroups.map((g, i) => ({ value: g, label: `Group ${i + 1} — runs together` })),
    { value: NEW_PARALLEL_GROUP, label: 'New parallel group' },
  ];

  async function submit(): Promise<void> {
    if (!stepName.trim()) { onError('Step name is required'); return; }
    setBusy(true);
    try {
      const resolvedGroup =
        !isParallelAllowed || !parallelGroup
          ? undefined
          : parallelGroup === NEW_PARALLEL_GROUP
            ? crypto.randomUUID()
            : parallelGroup;
      await createStep(token, definitionId, {
        stepSequence: nextSeq,
        stepName: stepName.trim(),
        stepType: stepType as WorkflowStep['stepType'],
        approverType: approverType ? (approverType as ApproverRule['approverType']) : undefined,
        approverRoleCode: approverRoleCode.trim() || undefined,
        allowDelegate,
        parallelGroupId: resolvedGroup,
      });
      setStepName(''); setApproverRoleCode(''); setApproverType(''); setParallelGroup('');
      onAdded();
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-label="add-step-form" style={{ ...sx.card }}>
      <h2 style={{ fontSize: 15, marginTop: 0 }}>Add step (sequence {nextSeq})</h2>
      <Field label="Step name" name="new-step-name" value={stepName} onChange={setStepName} required />
      <SelectField label="Step type" name="new-step-type" value={stepType} onChange={setStepType} options={toOptions(WORKFLOW_STEP_TYPES)} />
      <SelectField
        label="Approver type"
        name="new-approver-type"
        value={approverType}
        onChange={(v) => {
          setApproverType(v);
          // A role chosen for ROLE_BASED and then left behind on HIERARCHY
          // would still be sent, and the step would read as routed to a role
          // it does not use.
          if (v !== 'ROLE_BASED' && v !== 'SPECIFIC_USER') setApproverRoleCode('');
        }}
        options={toOptions(APPROVER_TYPES)}
        placeholder="— None —"
      />
      {roleApplies && (
        rolesError ? (
          <p role="alert" data-testid="roles-error" style={sx.error}>{rolesError}</p>
        ) : roles === null ? (
          <p data-testid="roles-loading" style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Loading roles…</p>
        ) : (
          <SelectField
            label="Approver role"
            name="new-approver-role"
            value={approverRoleCode}
            onChange={setApproverRoleCode}
            options={roles.map((r) => ({ value: r.roleCode, label: `${r.roleName} (${r.roleCode})` }))}
            placeholder="— Select a role —"
            required={approverType === 'ROLE_BASED'}
          />
        )
      )}
      {isParallelAllowed && (
        <>
          <SelectField
            label="Parallel group"
            name="new-parallel-group"
            value={parallelGroup}
            onChange={setParallelGroup}
            options={groupOptions}
            placeholder="— Runs on its own —"
          />
          <p data-testid="parallel-group-help" style={{ fontSize: 12, color: 'var(--color-text-muted)', margin: '0 0 10px' }}>
            Steps in the same group open together, so their approvers can
            decide at the same time. The level is only carried once every one
            of them has approved; if an approver is away, their delegate can
            act for them.
          </p>
        </>
      )}
      {/* "Mandatory" was removed, not hidden. It belonged to an older step
          shape where it marked branches that only ran sometimes — the Rework
          and Reject paths — alongside can_reject and can_return. That routing
          is now decided by conditions and by the on-approval / on-rejection
          actions, and nothing has read is_mandatory since. Leaving the box on
          screen asked an administrator a question whose answer changed
          nothing. */}
      <CheckboxField label="Allow delegate" name="new-allow-delegate" checked={allowDelegate} onChange={setAllowDelegate} />
      <div style={sx.actionBar}>
        <Button style={sx.primaryBtn} disabled={busy} aria-label="add-step" onClick={submit}>
          {busy ? 'Adding…' : 'Add step'}
        </Button>
      </div>
    </section>
  );
}

// ── Per-step configuration panel (conditions + approver rules + TAT) ──────────
function StepConfigPanel({ stepId, onError }: {
  readonly stepId: string;
  readonly onError: (m: string) => void;
}): ReactNode {
  return (
    <section aria-label="step-config-panel" style={{ ...sx.card, maxWidth: 900 }}>
      <ConditionsEditor stepId={stepId} onError={onError} />
      <ApproverRulesEditor stepId={stepId} onError={onError} />
      <TatEditor stepId={stepId} onError={onError} />
    </section>
  );
}

// ── Condition builder (T047) ──────────────────────────────────────────────────
function ConditionsEditor({ stepId, onError }: { readonly stepId: string; readonly onError: (m: string) => void }): ReactNode {
  const { token } = useAuth();
  const [rows, setRows] = useState<StepCondition[] | null>(null);
  const [fieldPath, setFieldPath] = useState('');
  const [operator, setOperator] = useState<string>('EQ');
  const [comparisonValue, setComparisonValue] = useState('');
  const [conjunction, setConjunction] = useState<string>('AND');
  const [isSkip, setIsSkip] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    listConditions(token, stepId).then(setRows).catch((e) => onError((e as Error).message));
  }, [token, stepId, onError]);
  useEffect(() => { load(); }, [load]);

  async function add(): Promise<void> {
    if (!fieldPath.trim()) { onError('Field path is required for a condition'); return; }
    setBusy(true);
    try {
      await createCondition(token, stepId, {
        fieldPath: fieldPath.trim(),
        operator: operator as StepCondition['operator'],
        comparisonValue: comparisonValue.trim() || undefined,
        logicalConjunction: conjunction as StepCondition['logicalConjunction'],
        isSkipCondition: isSkip,
      });
      setFieldPath(''); setComparisonValue('');
      load();
    } catch (e) {
      onError((e as Error).message);
    } finally { setBusy(false); }
  }

  async function remove(conditionId: string): Promise<void> {
    try { await deleteCondition(token, conditionId); load(); }
    catch (e) { onError((e as Error).message); }
  }

  return (
    <div aria-label="conditions-editor" style={{ marginBottom: 20 }}>
      <h3 style={{ fontSize: 14 }}>Conditions</h3>
      {!rows ? <p>Loading…</p> : rows.length === 0 ? (
        <p data-testid="conditions-empty" style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>No conditions.</p>
      ) : (
        <ul aria-label="conditions-list" style={{ paddingLeft: 18, fontSize: 13 }}>
          {rows.map((c) => (
            <li key={c.conditionId} data-testid={`condition-${c.conditionId}`} style={{ marginBottom: 4 }}>
              {c.logicalConjunction} · {c.fieldPath} {c.operator} {c.comparisonValue ?? ''} {c.isSkipCondition ? '(skip)' : ''}
              <Button style={{ ...sx.dangerBtn, marginLeft: 8, padding: '2px 8px' }}
                aria-label={`delete-condition-${c.conditionId}`} onClick={() => remove(c.conditionId)}>×</Button>
            </li>
          ))}
        </ul>
      )}
      <Field label="Field path" name="cond-field" value={fieldPath} onChange={setFieldPath} placeholder="e.g. amount" />
      <SelectField label="Operator" name="cond-operator" value={operator} onChange={setOperator} options={toOptions(CONDITION_OPERATORS)} />
      <Field label="Comparison value" name="cond-value" value={comparisonValue} onChange={setComparisonValue} />
      <SelectField label="Conjunction" name="cond-conjunction" value={conjunction} onChange={setConjunction} options={toOptions(LOGICAL_CONJUNCTIONS)} />
      <CheckboxField label="Skip condition" name="cond-skip" checked={isSkip} onChange={setIsSkip} />
      <div style={sx.actionBar}>
        <Button style={sx.primaryBtn} disabled={busy} aria-label="add-condition" onClick={add}>Add condition</Button>
      </div>
    </div>
  );
}

// ── Approver resolution builder (T048) ────────────────────────────────────────
function ApproverRulesEditor({ stepId, onError }: { readonly stepId: string; readonly onError: (m: string) => void }): ReactNode {
  const { token } = useAuth();
  const [rows, setRows] = useState<ApproverRule[] | null>(null);
  const [approverType, setApproverType] = useState<string>('ROLE_BASED');
  const [resolvedRoleCode, setResolvedRoleCode] = useState('');
  const [resolvedUserId, setResolvedUserId] = useState('');
  const [hierarchyOffset, setHierarchyOffset] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    listApproverRules(token, stepId).then(setRows).catch((e) => onError((e as Error).message));
  }, [token, stepId, onError]);
  useEffect(() => { load(); }, [load]);

  async function add(): Promise<void> {
    setBusy(true);
    try {
      await createApproverRule(token, stepId, {
        approverType: approverType as ApproverRule['approverType'],
        resolvedRoleCode: resolvedRoleCode.trim() || undefined,
        resolvedUserId: resolvedUserId || undefined,
        hierarchyOffset: hierarchyOffset ? Number(hierarchyOffset) : undefined,
      });
      setResolvedRoleCode(''); setResolvedUserId(''); setHierarchyOffset('');
      load();
    } catch (e) {
      onError((e as Error).message);
    } finally { setBusy(false); }
  }

  async function remove(ruleId: string): Promise<void> {
    try { await deleteApproverRule(token, ruleId); load(); }
    catch (e) { onError((e as Error).message); }
  }

  return (
    <div aria-label="approver-rules-editor" style={{ marginBottom: 20 }}>
      <h3 style={{ fontSize: 14 }}>Approver resolution rules</h3>
      {!rows ? <p>Loading…</p> : rows.length === 0 ? (
        <p data-testid="approver-rules-empty" style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>No approver rules.</p>
      ) : (
        <ul aria-label="approver-rules-list" style={{ paddingLeft: 18, fontSize: 13 }}>
          {rows.map((r) => (
            <li key={r.ruleId} data-testid={`approver-rule-${r.ruleId}`} style={{ marginBottom: 4 }}>
              #{r.ruleSequence} · {r.approverType} {r.resolvedRoleCode ? `role=${r.resolvedRoleCode}` : ''} {r.resolvedUserId ? `user=${personLabel(r.actorName ?? null, r.actorCode ?? null) ?? '—'}` : ''} {r.hierarchyOffset != null ? `offset=${r.hierarchyOffset}` : ''}
              <Button style={{ ...sx.dangerBtn, marginLeft: 8, padding: '2px 8px' }}
                aria-label={`delete-approver-rule-${r.ruleId}`} onClick={() => remove(r.ruleId)}>×</Button>
            </li>
          ))}
        </ul>
      )}
      <SelectField label="Approver type" name="rule-approver-type" value={approverType} onChange={setApproverType} options={toOptions(APPROVER_TYPES)} />
      <Field label="Resolved role code" name="rule-role" value={resolvedRoleCode} onChange={setResolvedRoleCode} />
      <UserPicker label="Resolved user" testId="rule-user" value={resolvedUserId} onPick={(id) => setResolvedUserId(id)} />
      <Field label="Hierarchy offset" name="rule-offset" value={hierarchyOffset} onChange={setHierarchyOffset} type="number" />
      <div style={sx.actionBar}>
        <Button style={sx.primaryBtn} disabled={busy} aria-label="add-approver-rule" onClick={add}>Add rule</Button>
      </div>
    </div>
  );
}

// ── TAT + escalation matrix (T049) ────────────────────────────────────────────
function TatEditor({ stepId, onError }: { readonly stepId: string; readonly onError: (m: string) => void }): ReactNode {
  const { number } = useBusinessNumber();
  const { token } = useAuth();
  const [rows, setRows] = useState<TatConfig[] | null>(null);
  const [tatValue, setTatValue] = useState('');
  const [tatUnit, setTatUnit] = useState<string>('HOURS');
  const [warn, setWarn] = useState('80');
  const [esc1, setEsc1] = useState('');
  const [esc2, setEsc2] = useState('');
  const [esc3, setEsc3] = useState('');
  const [autoAction, setAutoAction] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    listTat(token, stepId).then(setRows).catch((e) => onError((e as Error).message));
  }, [token, stepId, onError]);
  useEffect(() => { load(); }, [load]);

  async function add(): Promise<void> {
    if (!tatValue.trim() || Number.isNaN(Number(tatValue))) { onError('TAT value must be a number'); return; }
    setBusy(true);
    try {
      await createTat(token, {
        stepId,
        tatValue: Number(tatValue),
        tatUnit: tatUnit as TatConfig['tatUnit'],
        warningThresholdPercent: warn ? Number(warn) : undefined,
        escalationLevel1Hours: esc1 ? Number(esc1) : undefined,
        escalationLevel2Hours: esc2 ? Number(esc2) : undefined,
        escalationLevel3Hours: esc3 ? Number(esc3) : undefined,
        autoActionOnTimeout: autoAction || undefined,
      });
      setTatValue(''); setEsc1(''); setEsc2(''); setEsc3(''); setAutoAction('');
      load();
    } catch (e) {
      onError((e as Error).message);
    } finally { setBusy(false); }
  }

  async function remove(tatConfigId: string): Promise<void> {
    try { await deleteTat(token, tatConfigId); load(); }
    catch (e) { onError((e as Error).message); }
  }

  return (
    <div aria-label="tat-editor" style={{ marginBottom: 4 }}>
      <h3 style={{ fontSize: 14 }}>TAT &amp; escalation matrix</h3>
      {!rows ? <p>Loading…</p> : rows.length === 0 ? (
        <p data-testid="tat-empty" style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>No TAT configured.</p>
      ) : (
        <table aria-label="tat-list" style={{ ...sx.table, fontSize: 13 }}>
          <thead>
            <tr style={sx.theadTr}>
              <th style={sx.th}>TAT</th><th style={sx.th}>Warn %</th>
              <th style={sx.th}>Esc L1/L2/L3 (h)</th><th style={sx.th}>On timeout</th><th style={sx.thRight} />
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.tatConfigId} data-testid={`tat-${t.tatConfigId}`} style={sx.tbodyTr}>
                <td style={sx.td}>{t.tatValue} {t.tatUnit}</td>
                <td style={sx.td}>{number(t.warningThresholdPercent)}</td>
                <td style={sx.td}>{t.escalationLevel1Hours ?? '—'}/{t.escalationLevel2Hours ?? '—'}/{t.escalationLevel3Hours ?? '—'}</td>
                <td style={sx.td}>{t.autoActionOnTimeout ?? '—'}</td>
                <td style={sx.tdRight}>
                  <Button style={{ ...sx.dangerBtn, padding: '2px 8px' }}
                    aria-label={`delete-tat-${t.tatConfigId}`} onClick={() => remove(t.tatConfigId)}>×</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Field label="TAT value" name="tat-value" value={tatValue} onChange={setTatValue} type="number" />
      <SelectField label="TAT unit" name="tat-unit" value={tatUnit} onChange={setTatUnit} options={toOptions(TAT_UNITS)} />
      <Field label="Warning threshold %" name="tat-warn" value={warn} onChange={setWarn} type="number" />
      <Field label="Escalation L1 (hours)" name="tat-esc1" value={esc1} onChange={setEsc1} type="number" />
      <Field label="Escalation L2 (hours)" name="tat-esc2" value={esc2} onChange={setEsc2} type="number" />
      <Field label="Escalation L3 (hours)" name="tat-esc3" value={esc3} onChange={setEsc3} type="number" />
      <SelectField label="Auto action on timeout" name="tat-auto" value={autoAction} onChange={setAutoAction} options={toOptions(ESCALATION_ACTIONS)} placeholder="— None —" />
      <div style={sx.actionBar}>
        <Button style={sx.primaryBtn} disabled={busy} aria-label="add-tat" onClick={add}>Add TAT rule</Button>
      </div>
    </div>
  );
}
